import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import type { XButton } from '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-slider.js';
import '@adornis/popover/x-tooltip.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { APrint } from '@adornis/print/db/print.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import alarm from '@ils/assets/alarm.mp3';
import '@ils/osces/client/ils-osce-type.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { hasGlobalRole } from '@ils/permissions/hasRole.js';
import { subscribeExamSlotsGroupedByStartTime } from '@ils/slots/api/examSlotGroup.js';
import { checkForAnswersInSlots } from '@ils/slots/api/slot.js';
import type { ExamSlotGroup } from '@ils/slots/db/examSlotGroup.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { isTextOsceCategory, UserRole } from '@ils/static/enums.js';
import type { ILSThemeDefinition } from '@ils/theme/theme.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, skip, switchMap, take, takeUntil } from 'rxjs/operators';
import { subscribeAnswerBySlotIDs } from '../../api/answer.js';
import { sendMailToAllParticipantsOfScheduledExamDay } from '../../api/examday.js';
import type { Answer } from '../../db/answer.js';
import { ExamDay } from '../../db/examdays.js';
import { ILSFile } from '../../db/ils-files.js';
import { ILSUser } from '../../db/ils-user.js';
import { type LivekitRoom, subscribeActiveLivekitRooms } from '../../db/livekit.js';
import { OnlineStatus } from '../../db/online-status.js';
import { scheduleExam } from '../../db/scheduler.js';
import { ServerTime } from '../../db/time.js';
import { ExamToken, ILSToken } from '../../db/token.js';
import { compareArrays } from '../../db/utils.js';
import { generateParticipantsPdfFileName } from '../../standalone-components/PdfUtil.js';
import { ILSEvaluationInspectDialog } from '../components/ils-evaluation-inspect.js';
import { isSidebarOpen } from '../global-state.js';
import { ILSExtendExam } from './ils-extend-exam.js';
import './ils-loader.js';
import { ILSMissingTraineesDialog } from './ils-missing-trainees-dialog.js';
import { IlsTimelineDetailDialog } from './ils-timeline-detail-dialog.js';
import { onlineStatusDot } from './online-status-dot.js';

const examDaySelector = {
  ...ExamDay.roundCalculationSelector,
  _class: 1,
  _id: 1,
  slotIDs: 1,
  candidateIDs: 1,
  fallbackTesters: 1,
  osceTesterTuples: {
    _class: 1,
    testerIDs: 1,
    osceID: 1,
    osce: {
      _id: 1,
      taskType: 1,
      durationInMinutes: 1,
    },
  },
  location: { displayName: 1 },
  examCenter: 1,
} as const satisfies BaseQLSelectionSet<ExamDay>;

const answerSelector = {
  _id: 1,
  _class: 1,
  slotID: 1,
  type: 1,
  text: 1,
  fileId: 1,
  onlyTraineeFileId: 1,
  filesPresent: 1,
  file: ILSFile.allFields,
  reportedError: {
    _class: 1,
    errorType: 1,
    reportedAt: 1,
    reportedBy: 1,
    description: 1,
  },
} as const satisfies BaseQLSelectionSet<Answer>;

const slotGroupSelector = {
  _class: 1,
  startTime: 1,
  endTime: 1,
  slots: {
    _id: 1,
    _class: 1,
    candidateID: 1,
    tester: 1,
    roomID: 1,
    startTime: 1,
    endTime: 1,
    isFallbackSlot: 1,
    isCandidateMissing: 1,
    fallbackSlotID: 1,
    operatorNotes: 1,
    osceID: 1,
    isAudioLongEnough: 1,
    problemWasReported: 1,
    osce: {
      _id: 1,
      _class: 1,
      title: 1,
      taskType: 1,
      osceType: 1,
      conversationType: 1,
      durationInMinutes: 1,
      enableVideo: 1,
      startButtonDuration: 1,
    },
    answer: answerSelector,
  },
} as const satisfies BaseQLSelectionSet<ExamSlotGroup>;

const roomSelector = {
  _class: 1,
  name: 1,
  participants: 1,
} as const satisfies BaseQLSelectionSet<LivekitRoom>;

@customElement('ils-timeline-admin')
export class ILSTimelineAdmin extends ChemistryLitElement<ILSThemeDefinition> {
  @state() serverTime = new RXController(this, ServerTime.subscribeServerTime()());

  @state() public currentUser = new RXController(this, currentUser<ILSUser>());

  @state() size = new WindowSizeController(this);

  @state() private readonly _examDayID = routingState.searchParams.get('day');

  @state() public examDay = new RXController(
    this,
    this._examDayID.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      switchMap(id => subscribeByID(ExamDay, id)(examDaySelector)),
    ),
  );

  @state() public examSlots = new RXController(
    this,
    this.examDay.observable.pipe(
      filter(Boolean),
      switchMap(examDay => subscribeExamSlotsGroupedByStartTime(examDay._id)(slotGroupSelector)),
    ),
  );

  @state() public columns = new RXController(
    this,
    this.examSlots.observable.pipe(
      filter(Boolean),
      switchMap(examSlots =>
        of(examSlots.reduce((acc, group) => Math.max(acc, group.slots.filter(s => !s.isFallbackSlot).length), 0)),
      ),
    ),
  );

  @state() public answers = new RXController(
    this,
    this.examDay.observable.pipe(
      switchMap(day => (day ? subscribeAnswerBySlotIDs(day.slotIDs, 500)(answerSelector) : of([] as Answer[]))),
    ),
  );

  @state() public users = new RXController(
    this,
    this.examDay.observable.pipe(
      switchMap(day => {
        return day
          ? ILSUser.getEntityByID<ILSUser>(
              ILSUser._class,
              day.osceTesterTuples
                .flatMap(tuple => tuple.testerIDs)
                .concat(day.candidateIDs)
                .concat(day.fallbackTesters)
                .concat(day.examCenter ?? [])
                .filter(<T>(x: T): x is NonNullable<T> => !!x),
            )({
              _id: 1,
              _class: 1,
              username: 1,
              email: 1,
              ecommerce: { _class: 1, firstName: 1, lastName: 1, fullName: 1 },
            })
          : of([] as ILSUser[]);
      }),
    ),
  );
  refetchTokens = new BehaviorSubject<void>(undefined);

  @state() tokens = new RXController(
    this,
    combineLatest([this.examDay.observable, this.refetchTokens, this.currentUser.observable]).pipe(
      filter(([day, _, user]) => !!day && !!user),
      distinctUntilChanged(
        ([prevDay, _, prevUser], [currDay, __, currUser]) =>
          compareArrays(prevDay!.candidateIDs, currDay!.candidateIDs) && prevUser!._id === currUser!._id,
      ),

      switchMap(([day, _, currentUser]) =>
        day && hasPermission(currentUser, 'shortTokens', 'view')
          ? ILSToken.getShortTokenByUserID(day.candidateIDs)({
              _id: 1,
              _class: 1,
              createdAt: 1,
              userID: 1,
              shortToken: 1,
            })
          : of([] as ILSToken[]),
      ),
    ),
  );

  @state() examToken = new RXController(
    this,
    combineLatest([this.examDay.observable, this.refetchTokens, this.currentUser.observable]).pipe(
      filter(([examDay, _, user]) => !!examDay && !!user),
      switchMap(([examDay, _, user]) =>
        hasPermission(user, 'shortTokens', 'view')
          ? ExamToken.getTokenForExamDay(examDay!._id)(ExamToken.allFields)
          : of(null),
      ),
    ),
  );

  @state() schedulingRunning = false;

  @state() activeRooms = new RXController(this, subscribeActiveLivekitRooms()(roomSelector));

  @state() hasAnswers = new RXController(
    this,
    this.examDay.observable.pipe(
      filter(Boolean),
      switchMap(examDay => checkForAnswersInSlots(examDay.slotIDs)()),
    ),
  );

  @state() private readonly _onlineStatus = new RXController(this, OnlineStatus.subscribeOnlineUsers()());

  @state() private readonly _checkInRoomInfo = new RXController(
    this,
    combineLatest([this._examDayID, this.activeRooms.observable]).pipe(
      map(([examDayID, activeRooms]) => activeRooms?.find(room => room.name === examDayID)),
    ),
  );

  private readonly _alarm = new Audio(alarm);

  @state() private readonly _alarmVolume = new LocalStorageBehaviorController(this, 'alarm_volume', 1);

  override connectedCallback(): void {
    super.connectedCallback();

    this.currentUser.observable
      .pipe(
        filter(Boolean),
        take(1),
        map(user => {
          // TODO[pagePerms] this sidebar management thingy has to be done differently -> route enter?
          if (hasGlobalRole(user, UserRole.ADMIN)) isSidebarOpen.next(false);
        }),
        takeUntil(this.disconnected),
      )
      .subscribe();

    this.examSlots.observable
      .pipe(
        map(slots =>
          !slots?.length
            ? 0
            : slots.reduce(
                (acc, examSlotGroup) => acc + examSlotGroup.slots.filter(slot => slot.problemWasReported).length,
                0,
              ),
        ),
        distinctUntilChanged(),
        pairwise(),
        takeUntil(this.disconnected),
      )
      .subscribe(([before, now]) => {
        if (before < now && now) {
          this._alarm.pause();
          this._alarm.currentTime = 0;
          this._alarm.play();
        }
      });

    this._alarmVolume.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(alarmVolume => typeof alarmVolume === 'number' && (this._alarm.volume = alarmVolume));
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);

    this.examSlots.observable
      .pipe(
        filter(slots => !!slots?.length),
        take(1),
        takeUntil(this.disconnected),
      )
      .subscribe(slotGroups => {
        const groupIdx = slotGroups!.findIndex((g, idx) => {
          return g.endTime > DateTime.now();
        });
        if (groupIdx >= 0)
          requestAnimationFrame(() =>
            this.shadowRoot
              ?.querySelector(`#group-${groupIdx}`)
              ?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
          );
      });
    this.requestUpdate();
  }

  override render() {
    if (this.examSlots.isLoading)
      return html`
        <x-flex center crossaxis-center ${css({ width: '100%', height: '100%' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    const currentUser = this.currentUser.value;
    const users = this.users.value;
    const examDay = this.examDay.value;
    const examSlots = this.examSlots.value;
    if (!currentUser || !users || !examDay) return nothing;
    if (!examSlots?.length) return this.notScheduled();

    return html`
      <x-flex padding="md" space="md">
        ${this.controlButtons()}
        <x-flex horizontal crossaxis-center space="sm">
          <x-text> Standort: ${examDay.location?.displayName ?? '---'} </x-text>
          <x-text>
            Koordinatoren:
            ${users
              .filter(user => examDay.examCenter?.includes(user._id))
              .map(user => user.ecommerce?.fullName ?? user.username)
              .join(', ')}
          </x-text>
          ${permissionGuard({
            resource: 'shortTokens',
            action: 'view',
            guardedTemplate: html`
              <x-text> Exam Token: ${this.examToken.value?.shortToken ?? 'noch nicht generiert'} </x-text>
            `,
          })}
          <div flex></div>
          ${permissionGuard({
            resource: 'examdays',
            action: 'operate',
            guardedTemplate: html`
              <x-flex horizontal crossaxis-center space="sm">
                <x-icon> volume_up </x-icon>
                <x-slider
                  ${css({ display: 'flex' })}
                  min="0"
                  max="1"
                  step="0.05"
                  @value-picked=${(e: ValueEvent<number>) => this._alarmVolume.next(e.detail.value)}
                  .value=${this._alarmVolume.value}
                >
                </x-slider>
              </x-flex>
            `,
          })}
        </x-flex>
        <div ${css({ height: '1px', width: '100%', background: this.colors.neutral.shade(200) })}></div>
        ${this._checkInRoomDisplay()}
        <x-grid columns="max-content repeat(${this.columns.value}, 1fr) max-content 1fr" ${css({ marginTop: '30px' })}>
          <div
            ${css({
              width: '1px',
              background: this.colors.neutral.shade(200),
              gridColumn: '-3',
              gridRow: '1 / span 1000',
            })}
          ></div>
          <x-flex center padding="sm"> <x-text bold> Zeit </x-text> </x-flex>
          ${[...Array(this.columns.value)].map(
            (_, index) => html` <x-flex center padding="sm"> <x-text bold> Raum ${index + 1} </x-text> </x-flex> `,
          )}
          <x-flex center padding="sm"> <x-text bold> Fallback </x-text> </x-flex>

          ${repeat(
            examSlots,
            group => group.startTime.toMillis(),
            (group, index) => {
              const previousSlot = examSlots[index - 1];
              const minutesToLastSlot = previousSlot ? group.startTime.diff(previousSlot.endTime).as('minutes') : 0;
              const appendPause =
                previousSlot && minutesToLastSlot >= examDay.blockPause && minutesToLastSlot !== examDay.slotPause;
              const appendBigPause =
                previousSlot && minutesToLastSlot >= examDay.breakInMinutes && minutesToLastSlot !== examDay.slotPause;

              return html`
                ${appendPause || appendBigPause
                  ? html`
                      <x-flex center crossaxis-center padding="lg" ${css({ gridColumn: '1 / -4' })}>
                        <x-text ?bold=${appendBigPause}> Pause </x-text>
                      </x-flex>
                    `
                  : nothing}
                <x-flex id="group-${index}" padding="sm" center ${css({ gridColumn: '1' })}>
                  <x-text ?bold=${group.endTime >= DateTime.now()}>
                    ${group.startTime.toLocaleString(DateTime.TIME_SIMPLE)} -
                    ${group.endTime.toLocaleString(DateTime.TIME_SIMPLE)}
                  </x-text>
                </x-flex>
                ${repeat(
                  group.slots.filter(s => !s.isFallbackSlot),
                  slot => slot._id,
                  slot => this.slotDisplay(slot),
                )}
                ${group.slots.find(s => s.isFallbackSlot)
                  ? html`
                      <x-flex horizontal ${acss({ gridColumnEnd: '1', gridColumn: '7', maxWidth: '300px' })}>
                        ${repeat(
                          group.slots.filter(slot => slot.isFallbackSlot),
                          slot => this.slotDisplay(slot),
                        )}
                      </x-flex>
                    `
                  : html`
                      <x-flex center padding="sm" ${css({ gridColumnEnd: '-1', minWidth: '120px' })}>
                        ${group.slots.every(s => s.osce?.taskType === 'text')
                          ? html` <x-text bold> Frei </x-text> `
                          : html`<x-icon> close </x-icon> `}
                      </x-flex>
                    `}
              `;
            },
          )}
        </x-grid>
      </x-flex>
    `;
  }

  private controlButtons() {
    return html`
      <x-flex horizontal crossaxis-center end wrap space="md">
        ${permissionGuard({
          resource: 'examdays',
          action: 'printcandiates',
          guardedTemplate: html`
            <x-button
              mode="outline"
              icon="print"
              ?disabled=${!this.examDay.value?.slotIDs.length}
              @click=${async (e: CustomEvent) => {
                if (!this.examDay.value) return;
                const examDay = this.examDay.value;
                const button = e.currentTarget as XButton;
                button.loading = true;
                try {
                  if (examDay.slotIDs.length) {
                    const dayStart = examDay.start;
                    // TODO[middleware] protect operation via registerMiddleware or patch @adornis/print, permission needed is examdays.printcandidates
                    await openPopupWithLoadingScreen(
                      APrint.servePdfFromComponent(
                        'print-participants',
                        `examDay=${examDay._id}`,
                        generateParticipantsPdfFileName(dayStart),
                        10000,
                      )(),
                      {
                        loadingText: 'Lädt PDF...',
                        spinnerColor: this.colors.accent.string,
                      },
                    );
                  } else void XDialog.alert('Der ExamDay wurde noch nicht geplant.');
                } catch (e) {
                  const error = e as Error;
                  logger.error(error);
                  void XDialog.alert(error.message);
                }
                button.loading = false;
              }}
            >
              Teilnehmerliste
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'examdays',
          action: 'modifymissingcandidates',
          guardedTemplate: html`
            <x-button
              mode="outline"
              @click=${async () => {
                if (!this.examDay.value || !this.users.value) return;
                const examDay = this.examDay.value;
                const users = this.users.value;
                const candidates = users.filter(user => examDay.candidateIDs.includes(user._id));
                const slots = (this.examSlots.value ?? [])
                  .map(slotGroup => slotGroup.slots)
                  .filter(Boolean)
                  .flat();
                const missingCandidatesID = examDay.candidateIDs.filter(c =>
                  slots.some(slot => slot.candidateID === c && slot.isCandidateMissing),
                );
                const missingCandidates = missingCandidatesID.map(c => users.find(u => u._id === c));
                const promise = firstValueFrom(this.examSlots.observable.pipe(skip(1)));
                await ILSMissingTraineesDialog.showPopup({
                  props: {
                    examDayID: examDay._id,
                    candidates,
                    selectedCandidates: missingCandidates,
                  },
                  modal: true,
                }).then(didSave => (didSave ? XDialog.waitFor(promise) : null));
              }}
            >
              Fehlende Teilnehmer
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'shortTokens',
          action: 'create',
          data: {
            _id: '__ALL__',
            examDayID: this.examDay.value!._id,
            _meta: { allowedExamDayIDs: [this.examDay.value!._id] },
          },
          guardedTemplate: html`
            <x-button
              mode="outline"
              icon="vpn_key"
              @click=${async () => {
                if (!this.examDay.value) return;
                if (!(await XDialog.confirm('Diese tokens sind AB DIESEM ZEITPUNKT für 24h gültig.'))) return;
                await XDialog.waitFor(
                  Promise.all([
                    ILSToken.generateShortTokensForUsers(
                      this.examDay.value.candidateIDs,
                      this.examDay.value._id,
                    )({ _id: 1, _class: 1, shortToken: 1, userID: 1 }),
                    ExamToken.generateExamToken(this.examDay.value._id)({ _id: 1, _class: 1 }),
                  ]),
                );
                this.refetchTokens.next();
              }}
            >
              Generiere ShortTokens
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'examdays',
          action: 'sendinfomail',
          guardedTemplate: html`
            <x-button
              mode="outline"
              icon="mail"
              @click=${async () => {
                if (
                  await XDialog.showPopup<boolean>({
                    content: resolve =>
                      html`
                        <x-flex padding="md" space="md">
                          <x-text>
                            Möchtest du an alle Teilnehmer dieses Prüfungstages eine Mail mit den geplanten Zeiten
                            schicken?
                          </x-text>
                          <x-flex horizontal space-between>
                            <x-button
                              mode="outline"
                              href="mailto:?bcc=${encodeURIComponent(
                                this.users.value
                                  ?.filter(
                                    user => this.examDay.value && this.examDay.value.candidateIDs.includes(user._id),
                                  )
                                  .map(user => user.email)
                                  .join(';') ?? '',
                              )}"
                              @click=${() => resolve(false)}
                            >
                              Selbst verfassen
                            </x-button>
                            <x-flex horizontal space="md">
                              <x-button mode="outline" @click=${() => resolve(false)}> Abbrechen </x-button>
                              <x-button @click=${() => resolve(true)}> Ja </x-button>
                            </x-flex>
                          </x-flex>
                        </x-flex>
                      `,
                  }).catch(() => false)
                ) {
                  void XDialog.waitFor(
                    sendMailToAllParticipantsOfScheduledExamDay(this.examDay.value!._id)().catch(e =>
                      XDialog.alert('Es gab ein Problem beim Versenden der Mails: ' + e.message),
                    ),
                  ).then(() => XSnackbar.show('Erfolgreich Mails versandt'));
                }
              }}
            >
              Mail an Teilnehmer
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'examdays',
          action: 'modify',
          guardedTemplate: html`
            <x-button
              mode="outline"
              icon="edit"
              @click=${() => goTo({ path: `/start/editor-exam/${this.examDay.value._id}`, searchParams: NO_CHANGE })}
            >
              Prüfungstag
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'examdays',
          action: 'schedule',
          guardedTemplate: html`
            <x-button
              mode="outline"
              tone="error"
              icon="refresh"
              ?disabled=${false}
              @click=${async () => {
                if (!this.examDay.value) return logger.error('no examDay on reschedule?');
                if (!(await XDialog.confirm('Alle aktuell geplanten Slots werden gelöscht. Fortfahren?'))) return;
                this.schedulingRunning = true;
                // await ExamDay.deleteSlotsOfExamDay(state.examDay._id)');
                try {
                  await XDialog.waitFor(scheduleExam(this.examDay.value._id)(), 'Prüfungstag wird neu geplant...');
                  void XSnackbar.show('Planung abgeschlossen!');
                } catch (e) {
                  void XDialog.alert('Es gab ein Problem beim Planen dieses Prüfungstages: ' + (e as Error).message);
                }
                this.schedulingRunning = false;
              }}
            >
              Erneut planen
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'slots',
          action: 'extend',
          guardedTemplate: html`
            <x-button
              tone="error"
              @click=${async () => {
                try {
                  const promise = firstValueFrom(this.examSlots.observable.pipe(skip(1)));
                  await ILSExtendExam.showPopup<boolean>({ modal: true }).then(didExtend =>
                    didExtend ? XDialog.waitFor(promise) : null,
                  );
                } catch (e) {
                  logger.error(e);
                  XSnackbar.show('Beim verlängern der Slots ist etwas schief gelaufen.');
                }
              }}
            >
              Situation verlängern
            </x-button>
          `,
        })}
      </x-flex>
    `;
  }

  private slotDisplay(slot: Slot) {
    if (!this.users.value) return nothing;
    const now = DateTime.fromJSDate(this.serverTime.value || new Date());
    const activeRoom = this.activeRooms.value?.find(room => room.name === slot.roomID);
    const user = this.users.value.find(u => u._id === slot.candidateID);
    const tester = this.users.value.find(u => u._id === slot.tester);
    const answer = this.answers.value?.find(a => a.slotID === slot._id);
    const token = this.tokens.value?.find(t => t.userID === user?._id);
    const slotIsRunning = slot.startTime <= now && slot.endTime! > now;
    const textColor =
      slot.isCandidateMissing ||
      slot.problemWasReported ||
      (slotIsRunning && !activeRoom && slot.osce?.taskType === 'voice') ||
      (slotIsRunning && activeRoom && slot.osce?.taskType === 'voice' && activeRoom.participants.length < 2)
        ? this.colors.tone.error
        : slot.fallbackSlotID
        ? 'orange'
        : (slot.endTime ?? Infinity) < now
        ? this.colors.neutral.shade(400)
        : undefined;

    return html`
      <x-flex
        padding="sm"
        space="sm"
        slotid="${slot._id}"
        ${acss({
          minWidth: '0',
          cursor: 'pointer',
          background: slot.problemWasReported ? this.colors.tone.error.shade(100) : 'transparent',
          '&:hover': {
            background: slot.problemWasReported ? this.colors.tone.error.shade(200) : this.colors.neutral.shade(100),
          },
        })}
        @click=${() => {
          this._alarm.pause();
          void IlsTimelineDetailDialog.showPopup({
            props: {
              examSlot: slot,
              user,
              tester,
              token,
              examDay: this.examDay.value,
              examSlots: this.examSlots.value,
              answers: this.answers.value,
              activeRoom,
              serverTime: this.serverTime.value,
              tooFewMembersInActiveRoom:
                slotIsRunning && activeRoom && slot.osce?.taskType === 'voice' && activeRoom.participants.length < 2,
              noActiveRoomForActiveSlot: slotIsRunning && !activeRoom && slot.osce?.taskType === 'voice',
            },
          });
        }}
      >
        <x-flex horizontal crossaxis-center space="xs">
          ${slot.problemWasReported ? this.problemIcon(slot) : nothing}
          <ils-osce-type .osce=${slot.osce}></ils-osce-type>
          <x-text
            flex
            ellipsis
            .value=${user?.ecommerce?.fullName ?? user?.username ?? 'Kein Prüfling?'}
            title=${user?.ecommerce?.fullName ?? user?.username ?? 'Kein Prüfling?'}
            ${css({ color: textColor, minWidth: '0' })}
          >
          </x-text>
          ${slot.osce?.taskType === 'voice' &&
          slot.endTime! < DateTime.now() &&
          !slot.isAudioLongEnough &&
          answer &&
          answer.has('file')
            ? this.audioLengthAlertIcon(answer?.file?.durationOfAudio)
            : nothing}
          ${activeRoom
            ? permissionGuard({
                resource: 'slots',
                action: 'spectate',
                data: slot,
                guardedTemplate: html`
                  <x-button
                    mode="text"
                    icon="person_search"
                    @click=${e => {
                      e.stopPropagation();
                      goTo({ path: `/start/spectate/${slot._id}`, searchParams: NO_CHANGE });
                    }}
                  >
                    ${activeRoom.participants.length}
                  </x-button>
                `,
              })
            : permissionGuard({
                resource: 'ratings',
                action: 'viewothers',
                guardedTemplate: answer
                  ? html`
                      <x-icon
                        @click=${e => {
                          e.stopPropagation();
                          void ILSEvaluationInspectDialog.showPopup({
                            props: { slotID: slot._id, editable: false },
                          });
                        }}
                        ${css({
                          color: answer.isValid() ? this.colors.green : this.colors.neutral.shade(200),
                          userSelect: 'none',
                          cursor: 'pointer',
                        })}
                      >
                        ${answer.isValid() ? 'check' : 'refresh'}
                      </x-icon>
                    `
                  : isTextOsceCategory(slot.osce?.osceType) ||
                    (slot.endTime ?? -Infinity) > DateTime.fromJSDate(this.serverTime.value ?? new Date())
                  ? nothing
                  : this.missingAnswerIcon(),
              })}
        </x-flex>
        ${slot.osce?.taskType === 'voice'
          ? permissionGuard({
              resource: 'examdays',
              action: 'operate',
              guardedTemplate: html`
                <x-flex horizontal crossaxis-center space="sm" ${css({ position: 'relative' })}>
                  <x-text
                    ellipsis
                    .value=${tester?.ecommerce?.fullName ?? tester?.username ?? 'Kein Schauspieler?'}
                    title=${tester?.ecommerce?.fullName ?? tester?.username ?? 'Kein Schauspieler?'}
                    ${css({ color: textColor })}
                  >
                  </x-text>
                  ${this._onlineStatus.value?.includes(tester?._id) ? onlineStatusDot(this) : nothing}
                </x-flex>
              `,
            })
          : nothing}
      </x-flex>
    `;
  }

  private missingAnswerIcon() {
    return html`
      <x-icon ${css({ color: this.colors.red, cursor: 'default', userSelect: 'none' })}>
        priority_high
        <x-tooltip .options=${{ followCursor: false, duration: [100, 100] }}>
          <x-text> Es wurde keine Antwort hochgeladen! </x-text>
        </x-tooltip>
      </x-icon>
    `;
  }

  private audioLengthAlertIcon(audioLength?: number) {
    /**  convert length to minutes and round to 2 decimals */
    const prettyLength = audioLength ? Math.round((audioLength / 60) * 100) / 100 : '--';
    return html`
      <x-icon ${css({ color: this.colors.tone.error, userSelect: 'none' })}>
        graphic_eq
        <x-tooltip .options=${{ followCursor: false, duration: [100, 100] }}>
          <x-text> Die Audiodatei ist potentiell zu kurz: ${prettyLength} Minuten </x-text>
        </x-tooltip>
      </x-icon>
    `;
  }

  private problemIcon(slot?: Slot) {
    return html`
      <x-icon
        ${css({ color: this.colors.tone.error, cursor: 'pointer', userSelect: 'none' })}
        @click=${async e => {
          e.stopPropagation();
          this._alarm.pause();
          const confirm = await XDialog.confirm('Hier wurde ein Problem gemeldet. Wurde es bereits gelöst?', {
            highlightDecline: true,
          });
          if (confirm && slot) {
            slot.problemWasReported = false;
            await slot.save();
          }
        }}
      >
        report_problem
        <x-tooltip .options=${{ followCursor: false, duration: [100, 100] }}>
          <x-text> Der Kandidat hat ein Problem gemeldet! </x-text>
        </x-tooltip>
      </x-icon>
    `;
  }

  private notScheduled() {
    const allocatedTesters = this.examDay.value?.getAllocatedTesters() ?? 0;
    const maxTesters = this.examDay.value?.getMaxTesters();

    const allTestersAllocated = allocatedTesters > 0 && allocatedTesters === maxTesters;

    return html`
      <x-flex ${css({ width: '100%', height: '100%' })} center crossaxis-center space="md">
        <x-h2> Dieser Tag wurde noch nicht geplant </x-h2>
        <x-text>
          Es sind derzeit ${this.examDay.value?.candidateIDs.length ?? 'keine'} Prüflinge eingeschrieben und
          ${this.examDay.value?.osceTesterTuples.length ?? 'keine'} OSCEs zugewiesen.
        </x-text>

        <x-text>
          ${allTestersAllocated
            ? 'Alle'
            : `${this.examDay.value?.getAllocatedTesters()} von ${this.examDay.value?.getMaxTesters()}`}
          benötigten Schauspieler wurden zugewiesen.
        </x-text>

        <x-flex horizontal crossaxis-center space="md">
          ${permissionGuard({
            resource: 'examdays',
            action: 'schedule',
            guardedTemplate: html`
              <x-button
                ?disabled=${this.schedulingRunning}
                @click=${async () => {
                  if (!this.examDay.value) return logger.error('No examDay on schedule?');
                  try {
                    await XDialog.waitFor(
                      scheduleExam(this.examDay.value._id)().then(() =>
                        firstValueFrom(this.examSlots.observable.pipe(skip(1))),
                      ),
                      'Prüfungstag wird geplant...',
                    );
                    void XSnackbar.show('Planung abgeschlossen!');
                  } catch (e) {
                    void XDialog.alert('Es gab ein Problem beim Planen dieses Prüfungstages: ' + (e as Error).message);
                  }
                }}
              >
                Jetzt planen
              </x-button>
            `,
          })}
          ${permissionGuard({
            resource: 'examdays',
            action: 'modify',
            guardedTemplate: html`
              <x-button
                mode="outline"
                @click=${() => goTo({ path: `/start/editor-exam/${this.examDay.value._id!}`, searchParams: NO_CHANGE })}
              >
                Prüfungstag bearbeiten
              </x-button>
            `,
          })}
        </x-flex>
      </x-flex>
    `;
  }

  private _checkInRoomDisplay() {
    const room = this._checkInRoomInfo.value;
    if (!room?.participants?.length) return nothing;
    const testers = this.users.value?.filter(user => room.participants.includes(user._id)) ?? [];
    return html`
      <x-flex
        horizontal
        crossaxis-center
        wrap
        space="sm"
        w100
        ${css({ background: this.colors.neutral.shade(100), color: this.colors.on.neutral.shade(100) })}
        padding="md"
      >
        <x-icon ${css({ fontSize: '20px' })}> mic_external_on </x-icon>
        <x-text bold> Folgende Schauspieler befinden sich gerade im Check-in Raum: </x-text>
        <x-text> ${testers.map(tester => tester.ecommerce?.fullName ?? tester.username).join(', ')} </x-text>
      </x-flex>
    `;
  }
}
